
import React from "react"
import Helmet from "react-helmet"
import { Link } from "gatsby"
import Layout from "../components/layout"


export default () => (
    <Layout>
        <Helmet>
            <title>Olivia Cheng | Hire Me</title>
            <meta name="description" content="I make things for humans. I'm a senior designer leading teams in developing and applying thoughtful, inclusive approaches to difficult problems. If I sound like someone you’d like to work with, read on."/>
        </Helmet>
        <article className="text--lead u-measure">
            <h1 class="heading heading--large">
                <span role="img" aria-label="Thunderbolt">⚡</span>
                Hire Me
            </h1>
            <p>
                Hey, it's Olivia. <span role="img" aria-label="Waving hand">👋</span>
                <br/>
                I make things for humans. I'm a senior designer who thinks and builds in systems, and leads teams in developing and applying thoughtful, inclusive approaches to difficult problems. I'm deeply passionate about designing technology and services that purposefully improves people's lives, especially for vulnerable or underserved communities.
            </p>
            <p>
                If I sound like someone you’d like to work with, read on. 
                <span role="img" aria-label="Right-pointing arrow"> ➡️</span>
            </p>

            <section>
                <h1 class="heading heading--medium">
                    <span role="img" aria-label="Woman tipping hand">💁🏻‍♀️</span> What
                </h1>
                <p>
                    I’m looking for a role as the lead of a product design team at a place that cares deeply about people and understands the importance of design.
                </p>
            </section>
            
            <section>
                <h1 class="heading heading--medium">
                    <span role="img" aria-label="Globe">🌐</span> Where
                </h1>
                <p>I’m based in Washington DC. I can work remotely, but have a preference for a home base in DC.</p>
            </section>

            <section>
                <h1 class="heading heading--medium">
                    <span role="img" aria-label="Calendar">🗓️</span> When
                </h1>    
                <p>
                    I’m available to start in early July 2019, or potentially sooner under the right conditions.
                </p>
            </section>

            <section>
                <h1 class="heading heading--medium">
                    <span role="img" aria-label="Woman raising hand">🙋🏻</span> About Me
                </h1>    
                <p>
                    I’ve been designing and building things for people over a decade now. I do my best work when I get to break down complex problems and apply thoughtful, inclusive approaches— working systematically and creating scalable solutions wherever possible, and always rooting every design decision in the needs of the user, first and foremost.
                </p>
                <p>
                    I deliver complex products, defining their shape and breathing life into abstract visions, improving processes, practices and growing teams along the way. I’ve taken lead roles across all steps of the product development cycle, working collaboratively and cross-functionally to plan and conduct user research, facilitate workshops to deeply understand business and stakeholder requirements, create user flows and design prototypes, all the way through to building design systems and refining interactions in code.
                </p>
                <p>
                    I choose the right tool for the job and I build inclusively. I advocate for best practices around design systems, front-end performance, accessibility, and responsive development in order to deliver consistent experiences that are accessible to all people and performant across all platforms.
                
                </p>
                <p>
                    Some challenges I've enjoyed include leading and managing a distributed design team to develop a new eligibility application for <a className="link" href="https://www.navapbc.com/#healthcare-gov">HealthCare.gov</a> to replace legacy systems and provide over 7.5 million households with a streamlined, intuitive experience to get health coverage. I've also led a redesign and built from the ground up a data-rich interface for <a className="link" href="http://openstates.org">Open States</a>, an open-source product that allows the public to track bills, activity and other data aggregated from 50 state legislatures. Read the <Link className="link" to="/resume/">resume</Link> for more.
                </p>
                <p>
                    Most recently, I've been spending my time as a product design consultant for two DC startups, helping to develop new products and build up design practices.
                </p>
            </section>

            <section>
                <h1 class="heading heading--medium">
                    <span role="img" aria-label="Right-pointing hand">👉</span> About you
                </h1>    
                <p>
                    I'd be super excited to work at an organization that shares and supports my values:
                </p>
                <ul>
                    <li>
                        <strong class="heading heading--small">People</strong>
                        <br/>
                        Your products or services create large-scale, positive impact for the people that use them or are affected by them.
                        Your team respects and empathizes with the people who use your services.
                        Your team members respect the people they work with, regardless of their role and title.
                        You have an HR team.
                    </li>
                    <li>
                        <strong class="heading heading--small">Diversity and inclusivity</strong>
                        <br/>
                        You have diverse (non-men and non-white) folks in leadership roles.
                        Your hiring practices, work policies and team culture reflect your diverse and inclusive team.
                    </li>
                    <li>
                        <strong class="heading heading--small">Work-life balance and flexibility</strong>
                        <br/>
                        Your culture and practices ensures that your team works sustainable hours, and they can work from home when they need to.
                        Your team members consistently work about 40 hours per week.
                    </li>
                    <li>
                        <strong class="heading heading--small">Collaboration and growth</strong>
                        <br/>
                        Your team works together to solve problems, share knowledge and collaborate with people across different roles like engineering, design, product, and support.
                        You offer professional development opportunities, and your team members have the space to continue growing no matter what stage of their career they’re in.
                    </li>
                    <li>
                        <strong class="heading heading--small">Healthy lifestyles outside of work</strong>
                        <br/>
                        You provide competitive pay, comprehensive healthcare plans, sick/mental-health days, generous vacation and parental leave, and 401k matching.
                    </li>
                </ul>
                
                <p>Additionally, your design team…</p>
                <ul>
                    <li>Does research. Your designers talk to the people who use your product or services and advocate for their needs.</li>
                    <li>Collaborates tightly with product and engineering. Your engineers, product managers, and designers frequently have conversations to make the best design decision together.</li>
                    <li>Uses the tools best suited for the job, whether that means paper sketches, facilitating a workshop, or working in the codebase.</li>
                    <li>Has a voice at the helm of the ship. You have advocates for design and your users at strategic, decision-making levels.</li>
                </ul>
            </section>

            <section>
                <h1 class="heading heading--medium">
                    <span role="img" aria-label="Handshake">🤝</span> Let’s work together
                </h1>    
                <p>
                    Does this sound like we’d be a good fit for each other? Great. You should say hey if you meet the following criteria:
                </p>
                <ul>
                    <li>Your organization checks all of the boxes above. Not there yet? If your team is actively moving in this direction, I’m open to helping you get there if the other factors feel like a good fit.</li> 
                    <li>Your organization is currently seeking someone to lead or manage a product design team (or will be in the very near future).</li>
                    <li>You are the hiring manager for that role, and are willing to have an initial call or in-person chat with me before I apply.</li>
                    <li>Ideally, you are also someone who works closely with your organization’s design, engineering and product teams.</li>
                </ul>

                <p>Say hey by emailing me at <a className="link" href="mailto:olivia@heyitsolivia.com">olivia@heyitsolivia.com</a>, and include the following:</p>
                <ul>
                    <li>The name of your organization.</li>
                    <li>Where it's based, and whether remote work is supported.</li>
                    <li>What kind of organization it is (e.g. private/public company, non-profit, government).</li>
                    <li>A brief description of why the organization would be a good fit for me, and why I would be a good fit for your team.</li>
                    <li>A description of the role and/or a link to the relevant job posting.</li>
                    <li>Any additional information you think would be useful for me to know.</li>
                </ul>
            </section>
            
            <span class="heading heading--large" role="img" aria-label="Waving hand">👋</span>

        </article>
    </Layout>
)
